import {
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ITranslationMap,
  type ICustomMapping,
  type ICustomMappingHandler,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DocumentData,
  DocumentReference,
  Firestore,
  IBlobFilenamePair,
  WithPrimaryKey,
  resolveWithConcurrencyForAll,
  slugify,
  type WithRef,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import { type TranslationMapHandler } from './translation-map';

const UPSERT_BULK_CONCURRENCY = 10;

export const DELETE_CONCURRENCY = 10;

export abstract class BaseCustomMappingHandler<
  DestinationRefType extends object = DocumentData,
  DestinationValueType = unknown,
  AssociatedValueType = unknown,
> implements
    ICustomMappingHandler<
      DestinationRefType,
      DestinationValueType,
      AssociatedValueType
    >
{
  abstract customMapping: ICustomMapping;

  abstract downloadMapping(
    migration: WithRef<IPracticeMigration>
  ): Promise<void>;

  abstract getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair>;

  abstract uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: Blob
  ): Promise<void>;

  canHandle(customMapping: ICustomMapping): boolean {
    return isEqual(
      customMapping.metadata.type,
      this.customMapping.metadata.type
    );
  }

  async upsertRecord(
    record: Omit<
      ITranslationMap<DestinationRefType, DestinationValueType>,
      'resourceType'
    >,
    translationMap: TranslationMapHandler
  ): Promise<void> {
    const existingRecord = await translationMap.getBySource(
      record.sourceIdentifier,
      this.customMapping.metadata.type
    );

    if (existingRecord) {
      return translationMap.upsert({
        ...existingRecord,
        ...record,
        resourceType: this.customMapping.metadata.type,
      });
    }

    return translationMap.upsert({
      ...record,
      resourceType: this.customMapping.metadata.type,
    });
  }

  async upsertBulk(
    records: Omit<
      ITranslationMap<DestinationRefType, DestinationValueType>,
      'resourceType'
    >[],
    translationMap: TranslationMapHandler
  ): Promise<{
    failedRecords: IFailedCustomMappingRecord[];
  }> {
    const existingRecords = await translationMap.getByType<
      DestinationRefType,
      DestinationValueType
    >(this.customMapping.metadata.type);

    const existingRecordsMap = new Map(
      existingRecords.map((record) => [record.sourceIdentifier, record])
    );

    const recordsForUpsert = records.map((record) => {
      const foundExisting = existingRecordsMap.get(record.sourceIdentifier);

      return foundExisting ? { ...foundExisting, ...record } : record;
    });

    const failedRecords: IFailedCustomMappingRecord[] = [];

    try {
      await resolveWithConcurrencyForAll(
        recordsForUpsert,
        UPSERT_BULK_CONCURRENCY,
        async (record) => {
          try {
            await translationMap.upsert({
              ...record,
              resourceType: this.customMapping.metadata.type,
            });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `Error upserting record ${record.sourceIdentifier}`,
              {
                ...record,
                resourceType: this.customMapping.metadata.type,
              },
              error
            );
            failedRecords.push({
              sourceIdentifier: record.sourceIdentifier,
              sourceLabel: record.sourceLabel,
              reason: 'Upsert failed',
            });
          }
        }
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error upserting records', error);
    }

    return { failedRecords };
  }

  getRecords(
    translationMap: TranslationMapHandler
  ): Promise<
    WithPrimaryKey<ITranslationMap<DestinationRefType, DestinationValueType>>[]
  > {
    return translationMap.getByType<DestinationRefType, DestinationValueType>(
      this.customMapping.metadata.type
    );
  }

  getBySource(
    sourceId: string,
    translationMap: TranslationMapHandler
  ): Promise<
    | WithPrimaryKey<ITranslationMap<DestinationRefType, DestinationValueType>>
    | undefined
  > {
    return translationMap.getBySource(
      sourceId,
      this.customMapping.metadata.type
    );
  }

  getFileName(): string {
    return slugify(this.customMapping.metadata.label);
  }
}

export async function resolveMappedItem<T extends object>(
  itemId: string,
  mappings: WithPrimaryKey<ITranslationMap<T>>[],
  translationMap: TranslationMapHandler,
  resourceType: string
): Promise<WithRef<T>> {
  const foundMapping = mappings.find(
    (mapping) => mapping.sourceIdentifier === itemId
  )?.destinationIdentifier;

  if (foundMapping) {
    return Firestore.getDoc<T>(foundMapping);
  }

  const resolvedMapping = await translationMap.getBySource(
    itemId,
    resourceType
  );
  if (!resolvedMapping || !resolvedMapping.destinationIdentifier) {
    throw new Error(
      `Failed to resolve mapping for ${resourceType} with id ${itemId}`
    );
  }

  return Firestore.getDoc(
    resolvedMapping.destinationIdentifier as DocumentReference<T>
  );
}
